import toast from 'react-hot-toast';
import { ProductFormData, ProductColor } from './types';
import {
  Product,
  CreateProductDetailRequest,
} from '../../../types/product.types';
import { useProductStore } from '../../../stores/product.store';
import { productCategoryService } from '../../../services/product-category.service';
import { productThicknessCompatibilityService } from '../../../services/product-thickness-compatibility.service';
import {
  productColorService,
  CreateProductColorRequest,
} from '../../../services/product-color.service';
import { productDetailService } from '../../../services/product-detail.service';
import { productColorImageService } from '../../../services/product-color-image.service';
import { ImageOrder } from '../../../types/product-image.types';

export const useFormSubmission = () => {
  const { createProduct, updateProduct, uploadImages, isLoading } =
    useProductStore();

  const submitForm = async (
    data: ProductFormData,
    product: Product | null | undefined,
    thumbnailImages: File[],
    productColors: ProductColor[],
    productDetail: Partial<CreateProductDetailRequest>,
    setSubmitting: (value: boolean) => void,
    onSuccess: () => void,
  ) => {
    console.log('=== ENHANCED FORM SUBMIT STARTED ===');
    console.log('Form data:', data);
    console.log('Thumbnail images:', thumbnailImages);
    console.log('Product colors:', productColors);
    console.log('Product detail:', productDetail);

    // Validate categories
    const categoryIds = data.categoryIds.map((id) => parseInt(id));
    if (categoryIds.length === 0) {
      console.error('Validation failed: No categories selected');
      toast.error('Vui lòng chọn ít nhất một danh mục');
      return;
    }

    // Validate colors (at least one with name, auto-generate productNumber if missing)
    // Validate colors - both colorName and productNumber are required
    const validColors = productColors.filter(
      (color) =>
        color.colorName.trim() !== '' && color.productNumber.trim() !== '',
    );

    console.log('Valid colors:', validColors);
    if (validColors.length === 0) {
      console.error('Validation failed: No valid colors');
      toast.error('Vui lòng thêm ít nhất một màu sắc với tên và mã sản phẩm');
      return;
    }

    // Validate thumbnail (at least one color must be marked as thumbnail)
    const hasThumbnail = validColors.some((color) => color.isThumbnail);
    if (!hasThumbnail) {
      console.error('Validation failed: No thumbnail selected');
      toast.error('Vui lòng chọn ít nhất một màu làm ảnh đại diện');
      return;
    }

    // Validate product details
    if (
      !productDetail.frameMaterial ||
      !productDetail.frameShape ||
      !productDetail.frameType
    ) {
      console.error('Validation failed: Missing product details');
      toast.error('Vui lòng điền đầy đủ thông tin chi tiết sản phẩm');
      return;
    }

    try {
      setSubmitting(true);
      console.log('=== UPLOAD PHASE STARTED ===');

      let imageUrls: string[] = [];

      // Upload thumbnail images if any
      if (thumbnailImages.length > 0) {
        console.log('Uploading thumbnail images:', thumbnailImages);
        try {
          const uploadedUrls = await uploadImages(thumbnailImages);
          console.log('Uploaded thumbnail URLs:', uploadedUrls);
          imageUrls = [...imageUrls, ...uploadedUrls];
        } catch (uploadError) {
          console.error('Thumbnail upload failed:', uploadError);
          toast.error('Lỗi khi upload hình ảnh thumbnail');
          return;
        }
      }

      // Create product data
      const totalStock = validColors.reduce(
        (sum, color) => sum + color.stock,
        0,
      );
      const productData = {
        productName: data.productName,
        price: data.price,
        stock: totalStock,
        productType: data.productType,
        gender: data.gender,
        categoryId: categoryIds[0], // Use first category as primary
        brandId: parseInt(data.brandId),
        description: data.description || undefined,
        isSustainable: data.isSustainable || false,
        isNew: data.isNew || false,
        isBoutique: data.isBoutique || false,
        imageUrls,
      };

      console.log('=== PRODUCT CREATION PHASE ===');
      console.log('Product data to be created:', productData);

      let productId: number;

      if (product) {
        console.log('Updating existing product:', product.productId);

        // Validate existing product ID
        if (!product.productId || isNaN(Number(product.productId))) {
          throw new Error('Invalid existing product ID: ' + product.productId);
        }

        await updateProduct(product.productId, productData);
        productId = Number(product.productId);
        console.log('Product updated successfully');
        toast.success('Cập nhật sản phẩm thành công!');
      } else {
        console.log('Creating new product...');
        try {
          const createdProduct = await createProduct(productData);
          console.log('Created product response:', createdProduct);
          productId = createdProduct?.productId || 0;
          console.log('New product ID:', productId);

          if (!productId || isNaN(productId)) {
            throw new Error('Invalid product ID returned from createProduct');
          }

          toast.success('Tạo sản phẩm thành công!');
        } catch (createError) {
          console.error('Product creation failed:', createError);
          throw createError;
        }
      }

      // Validate productId before proceeding
      if (!productId || isNaN(productId)) {
        throw new Error('Invalid product ID: ' + productId);
      }

      // Update product categories
      if (categoryIds.length > 0 && productId) {
        console.log('=== UPDATING CATEGORIES PHASE ===');
        console.log(
          'Updating categories for product:',
          productId,
          'Categories:',
          categoryIds,
        );
        try {
          await productCategoryService.updateProductCategories(
            Number(productId),
            categoryIds,
          );
          console.log('Categories updated successfully');
          toast.success('Cập nhật danh mục sản phẩm thành công!');
        } catch (error) {
          console.error('Error updating product categories:', error);
          toast.error('Có lỗi khi cập nhật danh mục sản phẩm');
        }
      }

      // Update lens thickness compatibility
      if (
        data.lensThicknessIds &&
        data.lensThicknessIds.length > 0 &&
        productId
      ) {
        console.log('=== UPDATING LENS THICKNESS COMPATIBILITY PHASE ===');
        const lensThicknessIds = data.lensThicknessIds.map((id) =>
          parseInt(id),
        );
        console.log(
          'Updating lens thickness compatibility for product:',
          productId,
          'Lens thickness IDs:',
          lensThicknessIds,
        );
        try {
          await productThicknessCompatibilityService.updateProductCompatibilities(
            Number(productId),
            lensThicknessIds,
          );
          console.log('Lens thickness compatibility updated successfully');
          toast.success('Cập nhật độ dày lens thành công!');
        } catch (error) {
          console.error('Error updating lens thickness compatibility:', error);
          toast.error('Có lỗi khi cập nhật độ dày lens');
        }
      }

      // Create product colors and details
      if (productId && !product) {
        // Only for new products
        console.log('=== CREATING COLORS AND DETAILS PHASE ===');
        console.log('Creating colors and details for product:', productId);

        for (let index = 0; index < validColors.length; index++) {
          const colorData = validColors[index];
          console.log(
            `Creating color ${index + 1}/${validColors.length}:`,
            colorData.colorName,
          );
          try {
            const color = await productColorService.createProductColor(
              Number(productId),
              {
                productId: Number(productId),
                productVariantName:
                  colorData.productVariantName || colorData.colorName.trim(),
                productNumber: colorData.productNumber.trim(),
                colorName: colorData.colorName.trim(),
                stock: colorData.stock,
                isThumbnail: colorData.isThumbnail,
              } as CreateProductColorRequest,
            );
            console.log('Color created:', color);

            // Create product detail for this color
            const detailData = {
              productId: Number(productId),
              bridgeWidth: productDetail.bridgeWidth || 0,
              frameWidth: productDetail.frameWidth || 0,
              lensHeight: productDetail.lensHeight || 0,
              lensWidth: productDetail.lensWidth || 0,
              templeLength: productDetail.templeLength || 0,
              productNumber: 0,
              frameMaterial: productDetail.frameMaterial || '',
              frameShape: productDetail.frameShape || '',
              frameType: productDetail.frameType || '',
              bridgeDesign: productDetail.bridgeDesign || '',
              style: productDetail.style || '',
              springHinges: productDetail.springHinges || false,
              weight: productDetail.weight || 0,
              multifocal: productDetail.multifocal || false,
              lensThicknessIds:
                data.lensThicknessIds?.map((id) => parseInt(id)) || [],
            };

            console.log(
              'Creating detail for product:',
              productId,
              'Detail data:',
              detailData,
            );

            const detail =
              await productDetailService.createProductDetail(detailData);
            console.log('Detail created:', detail);

            // Upload color images to database
            console.log('=== UPLOADING COLOR IMAGES ===');
            const imageOrders: ImageOrder[] = ['a', 'b', 'c', 'd', 'e'];
            for (const imageOrder of imageOrders) {
              const imageFile = colorData.images[imageOrder];
              if (imageFile) {
                try {
                  console.log(
                    `Uploading image ${imageOrder} for color ${colorData.colorName}`,
                  );
                  const uploadedImage =
                    await productColorImageService.uploadProductColorImage({
                      productId: Number(productId),
                      colorId: color.id,
                      productNumber: colorData.productNumber.trim(),
                      imageOrder: imageOrder,
                      file: imageFile,
                    });
                  console.log(
                    `Image ${imageOrder} uploaded successfully:`,
                    uploadedImage,
                  );
                } catch (imageError) {
                  console.error(
                    `Failed to upload image ${imageOrder}:`,
                    imageError,
                  );
                  toast.error(
                    `Có lỗi khi upload ảnh ${imageOrder} cho màu ${colorData.colorName}`,
                  );
                }
              }
            }
            console.log(
              `Color images upload completed for ${colorData.colorName}`,
            );
          } catch (error) {
            console.error(
              `Error creating color ${colorData.colorName}:`,
              error,
            );
            toast.error(`Có lỗi khi tạo màu ${colorData.colorName}`);
          }
        }
        console.log('Colors and details creation completed');
        toast.success('Tạo màu sắc và chi tiết sản phẩm thành công!');
      }

      console.log('=== CALLING onSuccess() ===');

      // Add a small delay to see all toasts before navigation
      setTimeout(() => {
        console.log('Executing onSuccess callback...');
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess();
          console.log('onSuccess callback executed');
        } else {
          console.error('onSuccess is not a function:', onSuccess);
        }
      }, 1000);

      console.log('=== ENHANCED FORM SUBMIT COMPLETED SUCCESSFULLY ===');
    } catch (error) {
      console.error('=== ENHANCED FORM SUBMIT FAILED ===');
      console.error('Error details:', error);
      toast.error(
        product ? 'Cập nhật sản phẩm thất bại!' : 'Tạo sản phẩm thất bại!',
      );
    } finally {
      setSubmitting(false);
      console.log('=== ENHANCED FORM SUBMIT FINISHED ===');
    }
  };

  return {
    submitForm,
    isLoading,
  };
};
